import type {
  LockedStickerArtifactReference,
  LockedStickerProductArtifact,
  MixSheetConfiguration,
  MixSheetRecipe,
  MixSheetRecipeItem,
  SingleStickerConfiguration,
  SizeId,
  StickerConfiguration,
  StickerSheetOrder,
} from "@/domain/types";
import {
  isValidConfiguration,
  normalizeLine,
  validateConfiguration,
  type ValidationError,
} from "@/domain/validation";

export interface ShopifyLineEntryWithKey {
  key: string;
  value: string;
}

export interface ShopifyLineEntryWithName {
  name: string;
  value: string;
}

export type ShopifyLineEntry = ShopifyLineEntryWithKey | ShopifyLineEntryWithName;

export const SHOPIFY_CART_ATTRIBUTE_KEYS = {
  product: "marka_product",
  name: "marka_name",
  font: "marka_font",
  background: "marka_background",
  textColor: "marka_text_color",
  size: "marka_size",
  icon: "marka_icon",
  configuration: "marka_configuration",
  lockedArtifact: "marka_locked_artifact",
  quantityUnit: "marka_quantity_unit",
  stickersPerSheet: "marka_stickers_per_sheet",
} as const;

export const SHOPIFY_STICKER_PRODUCT_KEY = "name-sticker";
export const SHOPIFY_STICKER_NO_ICON_VALUE = "none";
export const SHOPIFY_STICKER_QUANTITY_UNIT_SHEET = "sheet";

export type StickerConfigurationParseFailureReason =
  | "missing_configuration"
  | "invalid_configuration_json"
  | "invalid_configuration";

export type LockedStickerArtifactParseFailureReason =
  | "missing_locked_artifact"
  | "invalid_locked_artifact_json"
  | "invalid_locked_artifact"
  | "mix_locked_artifact_missing_by_size";

export type StickerSheetOrderParseFailureReason =
  | "missing_sheet_order"
  | "invalid_sheet_order";

export type StickerConfigurationParseResult =
  | {
      ok: true;
      configuration: StickerConfiguration;
    }
  | {
      ok: false;
      reason: StickerConfigurationParseFailureReason;
      validationErrors?: ValidationError[];
    };

export type LockedStickerArtifactParseResult =
  | {
      ok: true;
      lockedArtifact: LockedStickerProductArtifact;
    }
  | {
      ok: false;
      reason: LockedStickerArtifactParseFailureReason;
      missingSizeIds?: SizeId[];
    };

export type StickerSheetOrderParseResult =
  | {
      ok: true;
      sheetOrder: StickerSheetOrder;
    }
  | {
      ok: false;
      reason: StickerSheetOrderParseFailureReason;
    };

function getEntryName(entry: ShopifyLineEntry): string {
  return "key" in entry ? entry.key : entry.name;
}

export function getShopifyLineEntryMap(entries: readonly ShopifyLineEntry[]): Map<string, string> {
  return new Map(entries.map((entry) => [getEntryName(entry), entry.value]));
}

export function getShopifyLineEntryValue(
  entryMap: ReadonlyMap<string, string>,
  key: keyof typeof SHOPIFY_CART_ATTRIBUTE_KEYS,
): string | undefined {
  return entryMap.get(SHOPIFY_CART_ATTRIBUTE_KEYS[key]);
}

export function getStickerProductKeyFromShopifyLineEntries(
  entries: readonly ShopifyLineEntry[],
): string | null {
  return getShopifyLineEntryValue(getShopifyLineEntryMap(entries), "product") ?? null;
}

function normalizeStickerConfiguration(
  value: unknown,
): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function normalizeStickerLines(rawLines: unknown) {
  if (!rawLines || typeof rawLines !== "object" || Array.isArray(rawLines)) {
    return undefined;
  }
  const source = rawLines as Record<string, unknown>;
  // Trim on read as well as on write. Lines are canonicalised before they reach
  // Shopify, but carts and orders placed before that was true can still carry
  // edge whitespace, and this is the path fulfillment reconstructs them from.
  return {
    line1: normalizeLine(typeof source.line1 === "string" ? source.line1 : ""),
    line2: normalizeLine(typeof source.line2 === "string" ? source.line2 : ""),
    line3: normalizeLine(typeof source.line3 === "string" ? source.line3 : ""),
  };
}

function normalizeIcon(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (value === SHOPIFY_STICKER_NO_ICON_VALUE) return null;
  return typeof value === "string" ? value : undefined;
}

function normalizeMixRecipe(value: unknown): MixSheetRecipe | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const items = (value as { items?: unknown }).items;
  if (!Array.isArray(items)) {
    return null;
  }
  const normalizedItems: MixSheetRecipeItem[] = [];
  for (const rawItem of items) {
    if (!rawItem || typeof rawItem !== "object") return null;
    const sizeId = (rawItem as { sizeId?: unknown }).sizeId;
    const count = (rawItem as { count?: unknown }).count;
    if (typeof sizeId !== "string" || !sizeId) return null;
    if (typeof count !== "number" || !Number.isInteger(count) || count < 1) return null;
    normalizedItems.push({ sizeId: sizeId as SizeId, count });
  }
  if (normalizedItems.length === 0) return null;
  return { items: normalizedItems };
}

export function parseStickerConfigurationFromShopifyLineEntries(
  entries: readonly ShopifyLineEntry[],
): StickerConfigurationParseResult {
  const entryMap = getShopifyLineEntryMap(entries);
  const rawConfiguration = getShopifyLineEntryValue(entryMap, "configuration");

  if (!rawConfiguration) {
    return {
      ok: false,
      reason: "missing_configuration",
    };
  }

  let parsed: Record<string, unknown> | null;
  try {
    parsed = normalizeStickerConfiguration(JSON.parse(rawConfiguration));
  } catch {
    return {
      ok: false,
      reason: "invalid_configuration_json",
    };
  }

  if (!parsed) {
    return {
      ok: false,
      reason: "invalid_configuration",
    };
  }

  const kind = parsed.kind === "mix" ? "mix" : "single";
  const lines = normalizeStickerLines(parsed.lines);
  const icon = normalizeIcon(parsed.icon);
  const quantity = typeof parsed.quantity === "number" ? parsed.quantity : undefined;

  if (kind === "mix") {
    const normalizedConfiguration: Partial<MixSheetConfiguration> = {
      kind: "mix",
      lines,
      font: typeof parsed.font === "string" ? parsed.font : undefined,
      background: typeof parsed.background === "string" ? parsed.background : undefined,
      textColor: typeof parsed.textColor === "string" ? parsed.textColor : undefined,
      icon,
      quantity,
      mixVariantId:
        typeof parsed.mixVariantId === "string" ? parsed.mixVariantId : undefined,
    };

    const validation = validateConfiguration(normalizedConfiguration);
    if (!validation.valid) {
      return {
        ok: false,
        reason: "invalid_configuration",
        validationErrors: validation.errors,
      };
    }

    if (!isValidConfiguration(normalizedConfiguration)) {
      return {
        ok: false,
        reason: "invalid_configuration",
      };
    }

    return {
      ok: true,
      configuration: normalizedConfiguration,
    };
  }

  const normalizedConfiguration: Partial<SingleStickerConfiguration> = {
    kind: "single",
    lines,
    font: typeof parsed.font === "string" ? parsed.font : undefined,
    background: typeof parsed.background === "string" ? parsed.background : undefined,
    textColor: typeof parsed.textColor === "string" ? parsed.textColor : undefined,
    size: typeof parsed.size === "string" ? parsed.size : undefined,
    icon,
    quantity,
  };

  const validation = validateConfiguration(normalizedConfiguration);

  if (!validation.valid) {
    return {
      ok: false,
      reason: "invalid_configuration",
      validationErrors: validation.errors,
    };
  }

  if (!isValidConfiguration(normalizedConfiguration)) {
    return {
      ok: false,
      reason: "invalid_configuration",
    };
  }

  return {
    ok: true,
    configuration: normalizedConfiguration,
  };
}

export function parseStickerSheetOrderFromShopifyLineEntries(
  entries: readonly ShopifyLineEntry[],
  options: {
    sheetCount: number;
  },
): StickerSheetOrderParseResult {
  const entryMap = getShopifyLineEntryMap(entries);
  const quantityUnit = getShopifyLineEntryValue(entryMap, "quantityUnit");
  const rawStickersPerSheet = getShopifyLineEntryValue(entryMap, "stickersPerSheet");

  if (!quantityUnit && !rawStickersPerSheet) {
    return {
      ok: false,
      reason: "missing_sheet_order",
    };
  }

  const stickersPerSheet = Number.parseInt(rawStickersPerSheet ?? "", 10);

  if (
    quantityUnit !== SHOPIFY_STICKER_QUANTITY_UNIT_SHEET ||
    !Number.isInteger(options.sheetCount) ||
    options.sheetCount < 1 ||
    !Number.isInteger(stickersPerSheet) ||
    stickersPerSheet < 1
  ) {
    return {
      ok: false,
      reason: "invalid_sheet_order",
    };
  }

  return {
    ok: true,
    sheetOrder: {
      quantityUnit: SHOPIFY_STICKER_QUANTITY_UNIT_SHEET,
      sheetCount: options.sheetCount,
      stickersPerSheet,
    },
  };
}

function parseLockedArtifactReference(
  value: unknown,
): LockedStickerArtifactReference | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const ref = value as Record<string, unknown>;

  if (
    ref.kind !== "canonical-svg" ||
    typeof ref.artifactId !== "string" ||
    typeof ref.rendererVersion !== "string" ||
    typeof ref.size !== "string" ||
    typeof ref.trimWidthPx !== "number" ||
    typeof ref.trimHeightPx !== "number" ||
    typeof ref.fullArtWidthPx !== "number" ||
    typeof ref.fullArtHeightPx !== "number" ||
    !Number.isFinite(ref.trimWidthPx) ||
    !Number.isFinite(ref.trimHeightPx) ||
    !Number.isFinite(ref.fullArtWidthPx) ||
    !Number.isFinite(ref.fullArtHeightPx) ||
    ref.trimWidthPx <= 0 ||
    ref.trimHeightPx <= 0 ||
    ref.fullArtWidthPx <= 0 ||
    ref.fullArtHeightPx <= 0
  ) {
    return null;
  }

  return {
    kind: "canonical-svg",
    artifactId: ref.artifactId,
    rendererVersion: ref.rendererVersion,
    size: ref.size as SizeId,
    trimWidthPx: ref.trimWidthPx,
    trimHeightPx: ref.trimHeightPx,
    fullArtWidthPx: ref.fullArtWidthPx,
    fullArtHeightPx: ref.fullArtHeightPx,
  };
}

export function parseLockedStickerArtifactFromShopifyLineEntries(
  entries: readonly ShopifyLineEntry[],
): LockedStickerArtifactParseResult {
  const entryMap = getShopifyLineEntryMap(entries);
  const rawLockedArtifact = getShopifyLineEntryValue(entryMap, "lockedArtifact");

  if (!rawLockedArtifact) {
    return {
      ok: false,
      reason: "missing_locked_artifact",
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawLockedArtifact);
  } catch {
    return {
      ok: false,
      reason: "invalid_locked_artifact_json",
    };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      ok: false,
      reason: "invalid_locked_artifact",
    };
  }

  const kind = (parsed as { kind?: unknown }).kind;

  if (kind === "single") {
    const ref = parseLockedArtifactReference((parsed as { ref?: unknown }).ref);
    if (!ref) {
      return { ok: false, reason: "invalid_locked_artifact" };
    }
    return {
      ok: true,
      lockedArtifact: { kind: "single", ref },
    };
  }

  if (kind === "mix") {
    const rawBySize = (parsed as { bySize?: unknown }).bySize;
    if (!rawBySize || typeof rawBySize !== "object" || Array.isArray(rawBySize)) {
      return { ok: false, reason: "invalid_locked_artifact" };
    }
    const recipe = normalizeMixRecipe((parsed as { recipe?: unknown }).recipe);
    if (!recipe) {
      return { ok: false, reason: "invalid_locked_artifact" };
    }
    const bySize: Partial<Record<SizeId, LockedStickerArtifactReference>> = {};
    for (const [sizeId, rawRef] of Object.entries(rawBySize as Record<string, unknown>)) {
      const ref = parseLockedArtifactReference(rawRef);
      if (!ref) {
        return { ok: false, reason: "invalid_locked_artifact" };
      }
      bySize[sizeId as SizeId] = ref;
    }
    const missingSizeIds = recipe.items
      .map((item) => item.sizeId)
      .filter((sizeId) => !bySize[sizeId]);
    if (missingSizeIds.length) {
      return {
        ok: false,
        reason: "mix_locked_artifact_missing_by_size",
        missingSizeIds,
      };
    }

    return {
      ok: true,
      lockedArtifact: { kind: "mix", bySize, recipe },
    };
  }

  return { ok: false, reason: "invalid_locked_artifact" };
}
